import {
  queryAllFeatures,
  queryFeatures,
} from '@esri/arcgis-rest-feature-service';
import { arcgisToGeoJSON } from '@terraformer/arcgis';
import type { Feature, FeatureCollection } from 'geojson';
import * as R from 'ramda';

import type { ArcGISFeatureSet, ArcGISIdsResponse } from './arcgisTypes';

const PAGE_CONCURRENCY = 6;
const DEFAULT_PAGE_SIZE = 1000;

/**
 * Compute a `maxAllowableOffset` (geometry generalization tolerance) in
 * degrees for a given Web-Mercator zoom level — roughly one screen pixel of
 * longitude at that zoom. Passing this to the ArcGIS `/query` makes the server
 * simplify (Douglas-Peucker) geometry before sending it, which drastically
 * reduces vertex count for dense line/polygon data with no visible difference
 * at the requested zoom. Since `outSR=4326`, the offset is in degrees.
 */
export const offsetForZoom = (zoom: number): number => {
  const TILE_SIZE = 256;
  const WORLD_DEGREES = 360;
  const safeZoom = Math.max(0, zoom);
  return WORLD_DEGREES / (TILE_SIZE * 2 ** safeZoom);
};

/**
 * Normalize an ArcGIS FeatureSet (or `arcgisToGeoJSON` output) into a clean
 * GeoJSON `FeatureCollection`, falling back to an empty collection on an
 * unexpected shape.
 */
const toFeatureCollection = (raw: unknown): FeatureCollection => {
  // arcgisToGeoJSON converts an ArcGIS FeatureSet (with .features[]) to a
  // GeoJSON FeatureCollection. The cast is needed because @terraformer/arcgis
  // ships no TypeScript declarations.
  const geojson = arcgisToGeoJSON(raw as any) as FeatureCollection;

  if (!geojson || geojson.type !== 'FeatureCollection') {
    return { type: 'FeatureCollection', features: [] };
  }

  return {
    type: 'FeatureCollection',
    features: (geojson.features ?? []) as Feature[],
  };
};

/**
 * Fetch one page of features by explicit object ids. Querying a fixed id list
 * (rather than `resultOffset`) guarantees the server returns exactly those
 * features — no overlap, no gaps — regardless of result ordering or how the
 * server clamps `resultRecordCount`. Geometry is reprojected to WGS-84 and
 * optionally generalized via `maxAllowableOffset`.
 */
const queryIdChunk = (
  layerUrl: string,
  objectIds: number[],
  maxAllowableOffset?: number,
): Promise<ArcGISFeatureSet> =>
  queryFeatures({
    url: layerUrl,
    objectIds,
    outFields: ['*'],
    outSR: '4326',
    httpMethod: 'POST',
    ...(maxAllowableOffset ? { maxAllowableOffset } : {}),
  }) as Promise<ArcGISFeatureSet>;

/**
 * Fetch every id chunk with bounded concurrency: chunks are split into groups
 * of `PAGE_CONCURRENCY` that run in parallel, with each group awaited before
 * the next starts. Returns the page FeatureSets in chunk order.
 */
const fetchChunksConcurrently = (
  layerUrl: string,
  idChunks: number[][],
  maxAllowableOffset?: number,
): Promise<ArcGISFeatureSet[]> =>
  R.splitEvery(PAGE_CONCURRENCY, idChunks).reduce<Promise<ArcGISFeatureSet[]>>(
    async (accPromise, group) => {
      const acc = await accPromise;
      const groupResults = await Promise.all(
        group.map(ids => queryIdChunk(layerUrl, ids, maxAllowableOffset)),
      );

      return [...acc, ...groupResults];
    },
    Promise.resolve([]),
  );

/**
 * Fall back to the library's sequential pager. Used when the service can't be
 * paged in parallel (no pagination support, or the metadata/count preflight
 * failed). Slower for large layers but always correct.
 */
const fetchAllSequential = async (
  layerUrl: string,
  maxAllowableOffset?: number,
): Promise<FeatureCollection> => {
  const arcgisResult = await queryAllFeatures({
    url: layerUrl,
    where: '1=1',
    outFields: ['*'],
    outSR: '4326',
    httpMethod: 'GET',
    ...(maxAllowableOffset ? { maxAllowableOffset } : {}),
  });
  return toFeatureCollection(arcgisResult);
};

/**
 * Ask the server for the complete, unpaged list of a layer's object ids in a
 * single `returnIdsOnly` query. Returns `null` when the service rejects the
 * query or lists no features — the caller then falls back to the sequential
 * pager. Because the list is unpaged, its length is also the exact feature
 * count, so the caller needs no separate `returnCountOnly` round trip.
 */
export const queryObjectIds = async (
  layerUrl: string,
): Promise<number[] | null> => {
  try {
    const result = (await queryFeatures({
      url: layerUrl,
      where: '1=1',
      returnIdsOnly: true,
      httpMethod: 'GET',
    })) as ArcGISIdsResponse;
    return Array.isArray(result.objectIds) && result.objectIds.length > 0
      ? result.objectIds
      : null;
  } catch {
    return null;
  }
};

/**
 * Fetch every feature from an already-resolved FeatureServer / queryable
 * MapServer layer URL, returning GeoJSON in WGS-84 (EPSG 4326).
 *
 * Fast path (id-chunked parallel fetch): given the layer's full `objectIds`
 * list, split it into chunks of the layer's honoured per-request cap
 * (`maxRecordCount`), then fetch the chunks by explicit `objectIds` with bounded
 * concurrency (`PAGE_CONCURRENCY` at a time).
 */
export const fetchLayerGeoJson = async (
  layerUrl: string,
  objectIds: number[] | null,
  options: { maxRecordCount?: number; maxAllowableOffset?: number } = {},
): Promise<FeatureCollection> => {
  const { maxRecordCount, maxAllowableOffset } = options;

  if (objectIds) {
    try {
      const chunkSize =
        maxRecordCount && maxRecordCount > 0
          ? maxRecordCount
          : DEFAULT_PAGE_SIZE;
      const idChunks = R.splitEvery(chunkSize, objectIds);

      const pages = await fetchChunksConcurrently(
        layerUrl,
        idChunks,
        maxAllowableOffset,
      );

      return toFeatureCollection({
        ...pages[0],
        features: pages.flatMap(page => page.features ?? []),
      });
    } catch {
      // Chunk fetch failed — fall through to the sequential pager.
    }
  }

  return fetchAllSequential(layerUrl, maxAllowableOffset);
};
